import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart, Star, Leaf, Eye } from 'lucide-react';
import { Product } from '../../types/product';
import { useCart } from '../../hooks/useCart';
import { formatCurrency, getDiscountPercentage } from '../../utils/format';
import { Button } from '../common';

interface ProductCardProps {
  product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const { addToCart, isInCart } = useCart();
  const inCart = isInCart(product.id);

  const discount = product.originalPrice 
    ? getDiscountPercentage(product.originalPrice, product.price)
    : 0;

  return (
    <div
      className="bg-dark-dark dark:bg-white rounded-xl shadow-sm border border-dark-light dark:border-gray-100 overflow-hidden group hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Container */}
      <div className="relative aspect-square overflow-hidden bg-dark-light dark:bg-gray-100">
        <img
          src={product.images[0]}
          alt={product.name}
          className={`w-full h-full object-cover transition-transform duration-500 ${
            isHovered ? 'scale-110' : 'scale-100'
          }`}
        />

        {/* Badges */}
        <div className="absolute top-2 left-2 md:top-3 md:left-3 flex flex-col gap-1.5 md:gap-2">
          {discount > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              -{discount}%
            </span>
          )}
          {product.organic && (
            <span className="bg-accent dark:bg-primary text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 transition-colors duration-300">
              <Leaf className="w-3 h-3" />
              <span className="hidden md:inline">Organic</span>
            </span>
          )}
        </div>

        {/* Action Buttons */}
        <div
          className={`absolute top-2 right-2 md:top-3 md:right-3 flex flex-col gap-1.5 md:gap-2 transition-all duration-300 ${
            isHovered ? 'opacity-100 translate-x-0' : 'opacity-100 md:opacity-0 md:translate-x-2'
          }`}
        >
          <button
            onClick={(e) => {
              e.preventDefault();
              setIsWishlisted(!isWishlisted);
            }}
            className={`p-2 md:p-2 rounded-full shadow-md transition-colors ${
              isWishlisted
                ? 'bg-red-500 text-white'
                : 'bg-dark dark:bg-white text-gray-300 dark:text-gray-600 hover:text-red-500'
            }`}
          >
            <Heart className={`w-5 h-5 ${isWishlisted ? 'fill-current' : ''}`} />
          </button>
          <Link
            to={`/product/${product.id}`}
            className="p-2 md:p-2 bg-dark dark:bg-white text-gray-300 dark:text-gray-600 rounded-full shadow-md hover:text-accent dark:hover:text-primary transition-colors duration-300"
          >
            <Eye className="w-5 h-5" />
          </Link>
        </div>

        {/* Add to Cart Button - Always visible on mobile */}
        <div
          className={`absolute bottom-2 left-2 right-2 md:bottom-3 md:left-3 md:right-3 transition-all duration-300 ${
            isHovered ? 'opacity-100 translate-y-0' : 'opacity-100 translate-y-0 md:opacity-0 md:translate-y-2 md:group-hover:opacity-100 md:group-hover:translate-y-0'
          }`}
        >
          <Button
            variant="primary"
            size="sm"
            className="w-full text-xs md:text-sm"
            leftIcon={<ShoppingCart className="w-3 h-3 md:w-4 md:h-4" />}
            onClick={(e) => {
              e.preventDefault();
              addToCart(product);
            }}
            disabled={!product.inStock || inCart}
          >
            {inCart ? 'In Cart' : product.inStock ? 'Add to Cart' : 'Out of Stock'}
          </Button>
        </div>
      </div>

      {/* Product Info */}
      <Link to={`/product/${product.id}`} className="block p-3 md:p-4">
        {/* Category */}
        <p className="text-xs text-accent dark:text-primary font-medium uppercase tracking-wide mb-1 transition-colors duration-300">
          {product.category}
        </p>

        {/* Name */}
        <h3 className="font-semibold text-white dark:text-dark-dark text-sm md:text-lg mb-1.5 md:mb-2 line-clamp-1 group-hover:text-accent dark:group-hover:text-primary transition-colors duration-300">
          {product.name}
        </h3>

        {/* Rating */}
        <div className="flex items-center gap-1.5 md:gap-2 mb-2 md:mb-3">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-3 h-3 md:w-4 md:h-4 ${
                  i < Math.floor(product.rating)
                    ? 'text-yellow-400 fill-current'
                    : 'text-gray-300 dark:text-gray-300'
                }`}
              />
            ))}
          </div>
          <span className="text-xs md:text-sm text-gray-400 dark:text-gray-500 transition-colors duration-300">
            ({product.reviews})
          </span>
        </div>

        {/* Price */}
        <div className="flex items-center gap-1.5 md:gap-2 flex-wrap">
          <span className="text-lg md:text-xl font-bold text-accent dark:text-primary transition-colors duration-300">
            {formatCurrency(product.price)}
          </span>
          {product.originalPrice && (
            <span className="text-xs md:text-sm text-gray-400 dark:text-gray-500 line-through transition-colors duration-300">
              {formatCurrency(product.originalPrice)}
            </span>
          )}
          <span className="text-xs md:text-sm text-gray-400 dark:text-gray-500 transition-colors duration-300">
            / {product.unit}
          </span>
        </div>

        {/* Stock Status */}
        <div className="mt-1.5 md:mt-2">
          {product.inStock ? (
            <span className="text-xs text-accent dark:text-primary font-medium transition-colors duration-300">
              In Stock ({product.stockQuantity})
            </span>
          ) : (
            <span className="text-xs text-red-500 font-medium">Out of Stock</span>
          )}
        </div>
      </Link>
    </div>
  );
};

export default ProductCard;